import { Box, Button } from "@mui/material"
import { Plus } from "lucide-react"
import { useState, type JSX } from "react"
import { useNavigate } from "react-router-dom"

import { useCan } from "@/auth/permissions"
import { useToast } from "@/components/ui/use-toast"
import { preShipApi } from "@crm-src/lib/pre-ship-api"

import { PreShipList } from "./pre-ship-list"

/**
 * The Pre-ship tab of a customer profile (2026-10-03): this customer's
 * pre-ship questionnaires — a customer may hold several, one per site or
 * system — and, for a holder of `crm:edit`, "New questionnaire", which
 * creates one at once (nothing to choose: crm-api starts it with Basic
 * information filled in) and opens it.
 */
export function PreShipTab({ customerId }: { customerId: string }): JSX.Element {
  const navigate = useNavigate()
  const { toast } = useToast()
  const canEdit = useCan("crm:edit")
  const [creating, setCreating] = useState(false)

  const create = async () => {
    setCreating(true)
    try {
      const q = await preShipApi.create({ customer_id: customerId })
      navigate(`/crm/pre-ship/${q.id}`)
    } catch (err: any) {
      toast({ title: "Not created", description: err?.response?.data?.error || "The questionnaire could not be created.", variant: "destructive" })
      setCreating(false)
    }
  }

  return (
    <Box data-cy="customer-preship-tab" sx={{ display: "grid", gap: 2 }}>
      {canEdit && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button size="small" variant="contained" startIcon={<Plus size={16} />} onClick={() => void create()} disabled={creating} data-cy="preship-tab-new">
            New questionnaire
          </Button>
        </Box>
      )}

      <PreShipList customerId={customerId} emptyMessage="No pre-ship questionnaire for this customer yet." />
    </Box>
  )
}
